import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CLIENT_PORTAL_AUTH_CHECKOUT_ENABLED,
  CLIENT_PORTAL_CLAIM_ENABLED,
  CLIENT_PORTAL_ENABLED,
} from "./clientPortalConfig";
import { clientPortalApi } from "./clientPortalApi";
import {
  AUTH_STATUS,
  CLIENT_PORTAL_ERROR,
} from "./clientPortalConstants";
import {
  normalizeClientPortalError,
  safeClientPortalMessage,
} from "./clientPortalErrors";
import {
  normalizeClientProfile,
  normalizeWhoAmI,
} from "./clientPortalNormalizers";
import {
  clearClientPortalRefreshToken,
  readClientPortalRefreshToken,
  writeClientPortalRefreshToken,
} from "./clientPortalStorage";

const ClientPortalContext = createContext(null);
const sharedRefreshByApi = new WeakMap();

function malformedSessionError() {
  const error = new Error("Client portal session is unavailable");
  error.category = CLIENT_PORTAL_ERROR.accountUnavailable;
  return error;
}

async function validateAccess(api, accessToken) {
  const identityRaw = await api.whoami(accessToken);
  const identity = normalizeWhoAmI(identityRaw);
  if (!identity) throw malformedSessionError();

  const clientRaw = await api.getClient(accessToken);
  const profile = normalizeClientProfile(clientRaw);
  if (!profile) throw malformedSessionError();

  return { identity, profile };
}

function refreshAndValidateShared(api, refreshToken) {
  const current = sharedRefreshByApi.get(api);
  if (current && current.refreshToken === refreshToken) return current.promise;

  const promise = (async () => {
    const tokenResponse = await api.refresh(refreshToken);
    const accessToken = String(tokenResponse?.access || "").trim();
    if (!accessToken) throw malformedSessionError();

    const session = await validateAccess(api, accessToken);
    return {
      ...session,
      accessToken,
      replacementRefreshToken: String(tokenResponse?.refresh || "").trim(),
    };
  })();

  sharedRefreshByApi.set(api, { refreshToken, promise });
  const clearSharedPromise = () => {
    const latest = sharedRefreshByApi.get(api);
    if (latest?.promise === promise) sharedRefreshByApi.delete(api);
  };
  promise.then(clearSharedPromise, clearSharedPromise);
  return promise;
}

export function ClientPortalProvider({
  children,
  api = clientPortalApi,
  enabled = CLIENT_PORTAL_ENABLED,
  claimEnabled = CLIENT_PORTAL_CLAIM_ENABLED,
  authenticatedCheckoutEnabled = CLIENT_PORTAL_AUTH_CHECKOUT_ENABLED,
}) {
  const [status, setStatus] = useState(
    enabled ? AUTH_STATUS.restoring : AUTH_STATUS.anonymous
  );
  const [profile, setProfile] = useState(null);
  const [authError, setAuthError] = useState("");
  const [sessionVersion, setSessionVersion] = useState(0);

  const mountedRef = useRef(true);
  const accessTokenRef = useRef("");
  const refreshTokenRef = useRef("");
  const refreshPromiseRef = useRef(null);
  const sessionEpochRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearSession = useCallback((nextStatus = AUTH_STATUS.anonymous) => {
    sessionEpochRef.current += 1;
    accessTokenRef.current = "";
    refreshTokenRef.current = "";
    clearClientPortalRefreshToken();
    if (!mountedRef.current) return;
    setProfile(null);
    setStatus(nextStatus);
    setSessionVersion((value) => value + 1);
  }, []);

  const applySession = useCallback((session, fallbackRefreshToken, expectedEpoch) => {
    if (
      !mountedRef.current ||
      sessionEpochRef.current !== expectedEpoch
    ) {
      return false;
    }
    const replacement = session.replacementRefreshToken;
    const refreshToken = replacement || fallbackRefreshToken;
    accessTokenRef.current = session.accessToken;
    refreshTokenRef.current = refreshToken;
    if (replacement) writeClientPortalRefreshToken(replacement);
    setProfile(session.profile);
    setStatus(AUTH_STATUS.authenticated);
    setAuthError("");
    setSessionVersion((value) => value + 1);
    return true;
  }, []);

  const refreshSession = useCallback(
    async ({ failureStatus = AUTH_STATUS.expired } = {}) => {
      if (refreshPromiseRef.current) return refreshPromiseRef.current;

      const refreshToken =
        refreshTokenRef.current || readClientPortalRefreshToken();
      if (!refreshToken) {
        clearSession(failureStatus);
        throw malformedSessionError();
      }

      const expectedEpoch = sessionEpochRef.current;
      const pending = (async () => {
        try {
          const session = await refreshAndValidateShared(api, refreshToken);
          if (!applySession(session, refreshToken, expectedEpoch)) {
            const error = malformedSessionError();
            error.status = 401;
            throw error;
          }
          return session.accessToken;
        } catch (error) {
          if (
            mountedRef.current &&
            sessionEpochRef.current === expectedEpoch
          ) {
            clearSession(failureStatus);
          }
          throw error;
        }
      })();

      refreshPromiseRef.current = pending;
      try {
        return await pending;
      } finally {
        if (refreshPromiseRef.current === pending) {
          refreshPromiseRef.current = null;
        }
      }
    },
    [api, applySession, clearSession]
  );

  useEffect(() => {
    if (!enabled) {
      clearSession(AUTH_STATUS.anonymous);
      return undefined;
    }

    const refreshToken = readClientPortalRefreshToken();
    if (!refreshToken) {
      setStatus(AUTH_STATUS.anonymous);
      return undefined;
    }

    refreshTokenRef.current = refreshToken;
    setStatus(AUTH_STATUS.restoring);
    refreshSession({ failureStatus: AUTH_STATUS.anonymous }).catch(() => {
      // Restoration failure intentionally returns to anonymous mode.
    });
    return undefined;
  }, [enabled, clearSession, refreshSession]);

  const login = useCallback(
    async ({ username, password }) => {
      if (!enabled) {
        return {
          ok: false,
          error: safeClientPortalMessage(CLIENT_PORTAL_ERROR.invalidLogin),
        };
      }

      setStatus(AUTH_STATUS.authenticating);
      setAuthError("");
      const expectedEpoch = sessionEpochRef.current + 1;
      sessionEpochRef.current = expectedEpoch;
      accessTokenRef.current = "";
      refreshTokenRef.current = "";
      clearClientPortalRefreshToken();
      setProfile(null);

      try {
        const tokenResponse = await api.login(username, password);
        const accessToken = String(tokenResponse?.access || "").trim();
        const refreshToken = String(tokenResponse?.refresh || "").trim();
        if (!accessToken || !refreshToken) throw malformedSessionError();

        const session = await validateAccess(api, accessToken);
        if (
          !mountedRef.current ||
          sessionEpochRef.current !== expectedEpoch
        ) {
          return {
            ok: false,
            error: safeClientPortalMessage(CLIENT_PORTAL_ERROR.invalidLogin),
          };
        }
        accessTokenRef.current = accessToken;
        refreshTokenRef.current = refreshToken;
        writeClientPortalRefreshToken(refreshToken);

        if (mountedRef.current) {
          setProfile(session.profile);
          setStatus(AUTH_STATUS.authenticated);
          setSessionVersion((value) => value + 1);
        }
        return { ok: true };
      } catch (error) {
        const message = safeClientPortalMessage(CLIENT_PORTAL_ERROR.invalidLogin);
        if (
          mountedRef.current &&
          sessionEpochRef.current === expectedEpoch
        ) {
          clearSession(AUTH_STATUS.anonymous);
          setAuthError(message);
        }
        return { ok: false, error: message };
      }
    },
    [api, clearSession, enabled]
  );

  const logout = useCallback(async () => {
    const accessToken = accessTokenRef.current;
    const refreshToken =
      refreshTokenRef.current || readClientPortalRefreshToken();
    clearSession(AUTH_STATUS.anonymous);
    if (mountedRef.current) setAuthError("");
    try {
      if (accessToken && refreshToken) {
        await api.logout(accessToken, refreshToken);
      }
    } catch (error) {
      // Local logout is authoritative for browser state even if revocation fails.
    }
  }, [api, clearSession]);

  const requestPrivate = useCallback(
    async (operation) => {
      const expectedEpoch = sessionEpochRef.current;
      let accessToken = accessTokenRef.current;
      if (!accessToken) {
        const error = malformedSessionError();
        error.status = 401;
        throw error;
      }

      try {
        const result = await operation(accessToken);
        if (sessionEpochRef.current !== expectedEpoch) {
          const error = malformedSessionError();
          error.status = 401;
          throw error;
        }
        return result;
      } catch (error) {
        if (error?.status !== 401) throw error;
      }

      if (sessionEpochRef.current !== expectedEpoch) {
        const error = malformedSessionError();
        error.status = 401;
        throw error;
      }
      accessToken = await refreshSession({
        failureStatus: AUTH_STATUS.expired,
      });
      if (sessionEpochRef.current !== expectedEpoch) {
        const error = malformedSessionError();
        error.status = 401;
        throw error;
      }
      try {
        const result = await operation(accessToken);
        if (sessionEpochRef.current !== expectedEpoch) {
          const error = malformedSessionError();
          error.status = 401;
          throw error;
        }
        return result;
      } catch (error) {
        if (
          error?.status === 401 &&
          sessionEpochRef.current === expectedEpoch
        ) {
          clearSession(AUTH_STATUS.expired);
        }
        throw error;
      }
    },
    [clearSession, refreshSession]
  );

  const loadQR = useCallback(
    () => requestPrivate((accessToken) => api.getQR(accessToken)),
    [api, requestPrivate]
  );
  const loadRegistrationCities = useCallback(
    (countyId) => api.getRegistrationCities(countyId),
    [api]
  );
  const loadRegistrationCounties = useCallback(
    () => api.getRegistrationCounties(),
    [api]
  );
  const registerClientAccount = useCallback(
    (payload) => api.registerClientAccount(payload),
    [api]
  );
  const loadPromotions = useCallback(
    () => requestPrivate((accessToken) => api.getPromotions(accessToken)),
    [api, requestPrivate]
  );
  const loadClaims = useCallback(
    () => requestPrivate((accessToken) => api.getClaims(accessToken)),
    [api, requestPrivate]
  );
  const loadDirectBenefits = useCallback(
    () => requestPrivate((accessToken) => api.getDirectBenefits(accessToken)),
    [api, requestPrivate]
  );
  const loadNews = useCallback(
    () => requestPrivate((accessToken) => api.getNews(accessToken)),
    [api, requestPrivate]
  );
  const claimPromotion = useCallback(
    (promotionId) =>
      requestPrivate((accessToken) =>
        api.claimPromotion(accessToken, promotionId)
      ),
    [api, requestPrivate]
  );
  const addAuthenticatedOrder = useCallback(
    (payload) =>
      requestPrivate((accessToken) =>
        api.addAuthenticatedOrder(accessToken, payload)
      ),
    [api, requestPrivate]
  );
  const registerAuthenticatedPay = useCallback(
    (orderId) =>
      requestPrivate((accessToken) =>
        api.registerAuthenticatedPay(accessToken, orderId)
      ),
    [api, requestPrivate]
  );
  const getAuthenticatedPayStatus = useCallback(
    (orderId) =>
      requestPrivate((accessToken) =>
        api.getAuthenticatedPayStatus(accessToken, orderId)
      ),
    [api, requestPrivate]
  );
  const cancelAuthenticatedOrder = useCallback(
    (orderId) =>
      requestPrivate((accessToken) =>
        api.cancelAuthenticatedOrder(accessToken, orderId)
      ),
    [api, requestPrivate]
  );

  const loadClientProfile = useCallback(async () => {
    const raw = await requestPrivate((accessToken) => api.getClient(accessToken));
    const nextProfile = normalizeClientProfile(raw);
    if (!nextProfile) throw malformedSessionError();
    return nextProfile;
  }, [api, requestPrivate]);

  const refreshClientProfile = useCallback(async () => {
    const nextProfile = await loadClientProfile();
    if (mountedRef.current) {
      setProfile(nextProfile);
    }
    return nextProfile;
  }, [loadClientProfile]);

  const refreshAfterOrderOutcome = useCallback(async () => {
    const expectedEpoch = sessionEpochRef.current;
    const offerRefreshes = profile?.emailValidationPending
      ? []
      : [loadPromotions(), loadClaims(), loadDirectBenefits()];
    await Promise.allSettled([
      refreshClientProfile(),
      ...offerRefreshes,
    ]);
    if (
      mountedRef.current &&
      sessionEpochRef.current === expectedEpoch
    ) {
      setSessionVersion((value) => value + 1);
    }
  }, [
    refreshClientProfile,
    loadPromotions,
    loadClaims,
    loadDirectBenefits,
    profile?.emailValidationPending,
  ]);

  const value = useMemo(
    () => ({
      enabled,
      claimEnabled: enabled && claimEnabled,
      authenticatedCheckoutEnabled:
        enabled && authenticatedCheckoutEnabled,
      status,
      profile,
      authError,
      sessionVersion,
      isAuthenticated: status === AUTH_STATUS.authenticated,
      isRestoring: status === AUTH_STATUS.restoring,
      sessionExpired: status === AUTH_STATUS.expired,
      login,
      logout,
      loadRegistrationCounties,
      loadRegistrationCities,
      registerClientAccount,
      loadQR,
      loadPromotions,
      loadClaims,
      loadDirectBenefits,
      loadNews,
      claimPromotion,
      addAuthenticatedOrder,
      registerAuthenticatedPay,
      getAuthenticatedPayStatus,
      cancelAuthenticatedOrder,
      refreshAfterOrderOutcome,
      loadClientProfile,
      refreshClientProfile,
      normalizeError: normalizeClientPortalError,
    }),
    [
      enabled,
      claimEnabled,
      authenticatedCheckoutEnabled,
      status,
      profile,
      authError,
      sessionVersion,
      login,
      logout,
      loadRegistrationCounties,
      loadRegistrationCities,
      registerClientAccount,
      loadQR,
      loadPromotions,
      loadClaims,
      loadDirectBenefits,
      loadNews,
      claimPromotion,
      addAuthenticatedOrder,
      registerAuthenticatedPay,
      getAuthenticatedPayStatus,
      cancelAuthenticatedOrder,
      refreshAfterOrderOutcome,
      loadClientProfile,
      refreshClientProfile,
    ]
  );

  return (
    <ClientPortalContext.Provider value={value}>
      {children}
    </ClientPortalContext.Provider>
  );
}

export function useClientPortal() {
  const value = useContext(ClientPortalContext);
  if (!value) {
    throw new Error("useClientPortal must be used inside ClientPortalProvider");
  }
  return value;
}
